'use client'

import { Dialog } from '@base-ui/react/dialog'
import * as stylex from '@stylexjs/stylex'
import { useRef, type ReactElement, type ReactNode } from 'react'

import { layers, colors, fonts, spacing, controlSizes } from '../tokens/tokens.stylex'

const styles = stylex.create({
  backdrop: {
    backgroundColor: colors.background,
    bottom: 0,
    insetInline: 0,
    position: 'fixed',
    top: 0,
    zIndex: layers.backdrop,
  },
  viewport: {
    bottom: 0,
    display: 'flex',
    insetInline: 0,
    position: 'fixed',
    top: 0,
    zIndex: layers.modal,
  },
  popup: {
    backgroundColor: colors.background,
    boxSizing: 'border-box',
    color: colors.foreground,
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    fontFamily: fonts.sans,
    minHeight: 0,
    minWidth: 0,
    outline: 'none',
    overflow: 'hidden',
  },
  header: {
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    flexShrink: 0,
    gap: spacing.space12,
    height: 65,
    paddingInline: spacing.space20,
    borderBottomColor: colors.border,
    borderBottomStyle: 'solid',
    borderBottomWidth: 1,
  },
  headerContent: { alignItems: 'center', display: 'flex', flex: 1, minWidth: 0 },
  title: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clipPath: 'inset(50%)',
    whiteSpace: 'nowrap',
    borderWidth: 0,
  },
  close: {
    alignItems: 'center',
    backgroundColor: 'transparent',
    borderWidth: 0,
    color: colors.foreground,
    cursor: 'pointer',
    display: 'flex',
    flexShrink: 0,
    height: controlSizes.touch,
    justifyContent: 'center',
    padding: 0,
    width: controlSizes.touch,
    outline: { default: 'none', ':focus-visible': `2px solid ${colors.foreground}` },
    outlineOffset: -2,
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0,
    overflowY: 'auto',
    paddingBlockEnd: spacing.space24,
  },
})

export type NavigationDrawerProps = {
  children: ReactNode
  header: ReactNode
  onOpenChange(open: boolean): void
  open: boolean
  title?: string
  trigger: ReactElement
}

/**
 * Use for controlled full-screen mobile navigation; choose NavigationMenu for grouped desktop destinations.
 * Applications supply the trigger, header, destinations, and visibility state. The drawer contains focus,
 * dismisses with Escape, and returns focus to its trigger. Status: supported.
 */
export function NavigationDrawer({
  children,
  header,
  onOpenChange,
  open,
  title = 'Navigation',
  trigger,
}: NavigationDrawerProps) {
  const closeRef = useRef<HTMLButtonElement>(null)

  return (
    <Dialog.Root open={open} onOpenChange={(next) => onOpenChange(next)}>
      <Dialog.Trigger render={trigger} />
      <Dialog.Portal>
        <Dialog.Backdrop {...stylex.props(styles.backdrop)} />
        <Dialog.Viewport {...stylex.props(styles.viewport)}>
          <Dialog.Popup initialFocus={closeRef} {...stylex.props(styles.popup)}>
            <Dialog.Title {...stylex.props(styles.title)}>{title}</Dialog.Title>
            <header {...stylex.props(styles.header)}>
              <div {...stylex.props(styles.headerContent)}>{header}</div>
              <Dialog.Close ref={closeRef} aria-label="Close navigation" {...stylex.props(styles.close)}>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                  <path d="m3 3 12 12M15 3 3 15" stroke="currentColor" strokeWidth="1.5" />
                </svg>
              </Dialog.Close>
            </header>
            <div {...stylex.props(styles.body)}>{children}</div>
          </Dialog.Popup>
        </Dialog.Viewport>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
